import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import { gql, useReactiveVar, useMutation } from "@apollo/client";
import ExperienceScreenUpdatePreference from "./ExperienceScreenUpdatePreference";
import { Helmet } from "react-helmet";
import { primaryActionLoadingVar, startPrimaryAction, completePrimaryAction } from "../helpers/client";
import { useAppBridge } from "@shopify/app-bridge-react";
import { Redirect } from "@shopify/app-bridge/actions";
import { Card } from "@tremor/react";
import TextInput from "./TextInput";
import { useUpdateThemeCache } from "../cache/ExperienceScreenUpdatePreferenceFunctions";
import { joinLinkFor } from "../helpers/tools";
import { currentShopifyProductVariantsVar } from "../helpers/client";
import ModalConfirmation from "./ModalConfirmation";
import DropboxPaperEditor from "./DropboxPaperEditor";
import Button, { ButtonVariant } from "./Button";
import CSVImport from "./CSVImport";
gql `
  fragment ExperienceScreenPreferences on Business {
    id
    alias
    name
    shopifyShop
    status
    shopifyVersion
    announcementLetter
    onlineUpsellLetter
    inStoreUpsellLetter
    team {
      id
      code
    }
    ...ExperienceScreenUpdatePreference
  }
`;
gql `
  mutation UpdateCampaignLetter($businessId: ID!, $letterInput: String!, $letterKind: LetterKind!) {
    updateCampaignLetter(businessId: $businessId, letterInput: $letterInput, letterKind: $letterKind) {
      id
      announcementLetter
      onlineUpsellLetter
      inStoreUpsellLetter
    }
  }
`;
export var ExperienceScreenPreferencesActions;
(function (ExperienceScreenPreferencesActions) {
    ExperienceScreenPreferencesActions["UpdatedExperienceScreenPreference"] = "Updated Experience Screen Preference";
    ExperienceScreenPreferencesActions["PreviewedExperienceScreenPreference"] = "Previewed Experience Screen Preference";
})(ExperienceScreenPreferencesActions || (ExperienceScreenPreferencesActions = {}));
const ExperienceScreenPreferences = ({ business, saveExperience }) => {
    var _a, _b, _c, _d, _e, _f;
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    const currentShopifyProducts = currentShopifyProductVariantsVar();
    const popupBlockerUrl = "https://help.withfriends.co/5975290-how-to-disable-pop-up-blockers-in-every-browser";
    let visitPopupBlockerPage = (e) => {
        e.preventDefault();
        window.top.location.href = popupBlockerUrl;
    };
    const popupBlockerWarning = (_jsxs("span", { children: ["If the links above don't work, please ", _jsx("a", { href: "#", style: { textDecoration: "underline" }, onClick: visitPopupBlockerPage, children: "disable your popup blocker." })] }));
    const embedHost = (process.env.REACT_APP_WITHFRIENDS_HOST == "https://withfriends.co"
        ? "live"
        : (process.env.REACT_APP_WITHFRIENDS_HOST == "https://sandbox.withfriends.cool"
            ? "sandbox"
            : "development"));
    let visitLink = (channel) => { };
    let visitPreview = (channel) => {
        let previewUrl = `https://${business.shopifyShop}.myshopify.com/?wf-preview=${channel}`;
        if (business.status !== 'Live') {
            previewUrl += `&wf-status=preview&wf-business-id=${business.id}&wf-host=${embedHost}`;
        }
        visitLink(previewUrl);
    };
    const [showCSV, setShowCSV] = useState(false);
    // TODO - can't seem to abstract this out without React crashing, so we gotta copy and paste it into any relevant file
    let app, redirect = null;
    try {
        app = useAppBridge();
        redirect = Redirect.create(app);
        visitPopupBlockerPage = visitPopupBlockerPage = (e) => {
            e.preventDefault();
            redirect.dispatch(Redirect.Action.REMOTE, { url: popupBlockerUrl });
        };
        visitLink = (link) => {
            redirect.dispatch(Redirect.Action.REMOTE, { url: link, newContext: true });
        };
    }
    catch (e) { }
    // TODO - end
    const updateBadgeText1 = useUpdateThemeCache((_a = business === null || business === void 0 ? void 0 : business.theme) === null || _a === void 0 ? void 0 : _a.id, "badgeText1");
    const updateBadgeText2 = useUpdateThemeCache((_b = business === null || business === void 0 ? void 0 : business.theme) === null || _b === void 0 ? void 0 : _b.id, "badgeText2");
    const updateBadgeAlignment = useUpdateThemeCache((_c = business === null || business === void 0 ? void 0 : business.theme) === null || _c === void 0 ? void 0 : _c.id, "badgeAlignment");
    const updateCornerRoundness = useUpdateThemeCache((_d = business === null || business === void 0 ? void 0 : business.theme) === null || _d === void 0 ? void 0 : _d.id, "cornerRoundness");
    const updateBadgeInverted = useUpdateThemeCache((_e = business === null || business === void 0 ? void 0 : business.theme) === null || _e === void 0 ? void 0 : _e.id, "badgeInverted");
    const updateBadgeFontSize = useUpdateThemeCache((_f = business === null || business === void 0 ? void 0 : business.theme) === null || _f === void 0 ? void 0 : _f.id, "badgeFontSize");
    useEffect(() => {
        // mixpanel.track_links(".profile-screen-preview", PrimaryAction, (el: HTMLElement) => ({
        //   action: ProfileScreenPreferencesActions.PreviewedProfileScreenPreference,
        //   preference: el.dataset.preference,
        // }))
    }, []);
    const user = business.team[0];
    const [showLetterEditor, setShowLetterEditor] = useState(false);
    const [editingLetterKind, setEditingLetterKind] = useState(null);
    const [letterDraft, setLetterDraft] = useState('');
    const [doUpdateCampaignLetter] = useMutation(gql `
    mutation UpdateCampaignLetter($businessId: ID!, $letterInput: String!, $letterKind: LetterKind!) {
      updateCampaignLetter(businessId: $businessId, letterInput: $letterInput, letterKind: $letterKind) {
        id
        announcementLetter
        onlineUpsellLetter
        inStoreUpsellLetter
      }
    }
  `);
    const openLetterEditor = useCallback((kind) => {
        setEditingLetterKind(kind);
        // Initialize draft from server values
        if (kind === 'ANNOUNCEMENT')
            setLetterDraft(((business === null || business === void 0 ? void 0 : business.announcementLetter) || ''));
        if (kind === 'ONLINE_UPSELL')
            setLetterDraft(((business === null || business === void 0 ? void 0 : business.onlineUpsellLetter) || ''));
        if (kind === 'IN_STORE_UPSELL')
            setLetterDraft(((business === null || business === void 0 ? void 0 : business.inStoreUpsellLetter) || ''));
        setShowLetterEditor(true);
    }, [business]);
    const closeLetterEditor = useCallback(() => {
        setShowLetterEditor(false);
        setEditingLetterKind(null);
        setLetterDraft('');
    }, []);
    const onLetterChange = useCallback((change) => {
        setLetterDraft(change.html);
    }, []);
    const saveLetter = useCallback(async () => {
        try {
            startPrimaryAction();
            if (!editingLetterKind)
                return;
            await doUpdateCampaignLetter({
                variables: {
                    businessId: business.id,
                    letterInput: letterDraft || '',
                    letterKind: editingLetterKind,
                },
            });
        }
        catch (e) {
        }
        finally {
            completePrimaryAction();
            setShowLetterEditor(false);
            setEditingLetterKind(null);
        }
    }, [letterDraft, doUpdateCampaignLetter, business === null || business === void 0 ? void 0 : business.id, editingLetterKind]);
    const showPreview = async (previewKind, variant) => {
        var _a, _b;
        let targetUrl = null;
        switch (previewKind) {
            case 'enableDashboard':
            case 'enableAccountPageLink':
                visitLink('https://www.loom.com/share/c3711ddb38754d2d97732339c87cb875');
                break;
            case 'enableFAQ':
            case 'enableNavigationLink':
                visitPreview(previewKind);
                break;
            case 'enableBadge':
                startPrimaryAction();
                await saveExperience();
                completePrimaryAction();
                visitPreview(previewKind);
                break;
            case 'enableAddToCartUpsell':
                //TODO:  I don't have time to fix this
                const productLink = (_a = currentShopifyProducts === null || currentShopifyProducts === void 0 ? void 0 : currentShopifyProducts[0]) === null || _a === void 0 ? void 0 : _a.onlineStoreUrl;
                let previewLink = productLink + "?wf-preview=" + previewKind;
                if (business.status !== 'Live') {
                    previewLink += `&wf-status=preview&wf-business-id=${business.id}&wf-host=${embedHost}`;
                }
                visitLink(previewLink);
                break;
            case 'enableSidebarUpsell':
                //TODO:  I don't have time to fix this
                const productLink2 = (_b = currentShopifyProducts === null || currentShopifyProducts === void 0 ? void 0 : currentShopifyProducts[0]) === null || _b === void 0 ? void 0 : _b.onlineStoreUrl;
                let previewLink2 = productLink2 + "?wf-preview=" + previewKind;
                if (business.status !== 'Live') {
                    previewLink2 += `&wf-status=preview&wf-business-id=${business.id}&wf-host=${embedHost}`;
                }
                visitLink(previewLink2);
                break;
            case 'enableSmsUpsell':
                targetUrl = `${process.env.REACT_APP_WITHFRIENDS_HOST}/${business.alias}/preview:sms_upsell=1,id=${user === null || user === void 0 ? void 0 : user.id},code=${user === null || user === void 0 ? void 0 : user.code},is_embedded`;
                //@ts-ignore
                window.Withfriends.interface.openPopup(null, { targetUrl: targetUrl, width: 500, clickLightboxToClose: true });
                break;
            case "enableEmailUpsell":
                targetUrl = `${process.env.REACT_APP_WITHFRIENDS_HOST}/${business.alias}/preview:upsell=1,id=${user === null || user === void 0 ? void 0 : user.id},code=${user === null || user === void 0 ? void 0 : user.code},is_embedded${variant === 'online' ? ',force_online=1' : ''}${variant === 'instore' ? ',force_in_store=1' : ''}`;
                //@ts-ignore
                window.Withfriends.interface.openPopup(null, { targetUrl: targetUrl, width: 900, clickLightboxToClose: true });
                break;
            case "enablePastCustomerUpsell":
                targetUrl = `${process.env.REACT_APP_WITHFRIENDS_HOST}/${business.alias}/preview:announcement=1,id=${user === null || user === void 0 ? void 0 : user.id},code=${user === null || user === void 0 ? void 0 : user.code},is_embedded`;
                //@ts-ignore
                window.Withfriends.interface.openPopup(null, { targetUrl: targetUrl, width: 900, clickLightboxToClose: true });
                break;
            case "enableRedirectUpsell":
                //@ts-ignore
                window.Withfriends.cart.showUpsell();
                break;
        }
    };
    const assetHost = process.env.REACT_APP_ASSET_HOST;
    const embedScript = `${assetHost}/api/shopifyV2.js?v=${business.shopifyVersion}&checkout=1&badge=1&embed=1&id=${business.id}&host=${embedHost}&preview=1&tone=transactional`;
    const preferences = [
        ...business.shopifyVersion == 2 ? [
            {
                label: "Member dashboard",
                option: "enableDashboard",
                required: true,
                description: "Allow members to change preferences, pause, upgrade, and cancel their membership",
                channel: "dashboard",
                previewKind: "video"
            },
            {
                label: "Manage my memberships button",
                option: "enableAccountPageLink",
                required: true,
                description: "Allow members to visit their member dashboard from their account page",
                channel: "accountlink",
                previewKind: "video"
            },
            {
                label: "Add to cart upsell",
                option: "enableAddToCartUpsell",
                recommended: true,
                description: `Upsell customers into subscribers with a deal when they add an product to their cart`,
                channel: "add-to-cart",
                previewKind: "live"
            },
            {
                label: "Add to cart upsell in sidebar",
                option: "enableSidebarUpsell",
                recommended: true,
                description: `Upsell customers into subscribers with a deal in your sidebar cart (requires sidebar cart enabled theme).  Compatible with add to cart upsell.`,
                channel: "sidebar",
                previewKind: "live"
            },
            {
                label: "Navigation link",
                option: "enableNavigationLink",
                recommended: true,
                description: `Let visitors subscribe to ${business.name} from any page`,
                channel: "navigation",
                previewKind: "live"
            },
            {
                label: "Member FAQ",
                option: "enableFAQ",
                recommended: true,
                description: "Let members answer their own questions and contact our support team directly for help",
                channel: "faq",
                previewKind: "live"
            },
        ] : [],
        Object.assign(Object.assign({ label: "Badge", option: "enableBadge" }, (business.shopifyVersion == 2 ? { recommended: true } : { required: true })), { description: `Let visitors subscribe to ${business.name} from any page` + (business.shopifyVersion == 2 ? ", and acknowledge their member status when logged in" : ""), channel: "badge", previewKind: "live" }),
        {
            option: "enableRedirectUpsell",
            label: "Post-checkout upsell",
            recommended: true,
            description: `Upsell customers into subscribers with a deal after checkout on the confirmation screen`,
            channel: "popup",
            previewKind: "popup"
        },
        {
            option: "enableEmailUpsell",
            label: "Enable email upsell",
            description: `Upsell customers into subscribers with a deal by email ` + (business.enableSmsUpsell ? "on the day" : "immediately") + ` after each purchase`,
            channel: "email",
            previewKind: "message"
        },
        {
            option: "enableSmsUpsell",
            label: "Text message upsell",
            description: `Upsell customers into subscribers with a deal by text immediately after each purchase`,
            channel: "text",
            previewKind: "message"
        },
        ...(business.shopifyVersion != 2) ? [{
                option: "enableTips",
                label: "Enable tips",
                description: `Allow customers to tip your business after each purchase`,
                channel: "popup",
            }] : [],
        {
            option: "enablePastCustomerUpsell",
            label: "Announce your memberships",
            description: `Send personalized membership deals to your existing customers automatically`,
            channel: "email",
            previewKind: "message"
        },
    ];
    return (_jsxs("div", { className: "w-full flex flex-wrap justify-center mb-4", children: [_jsx(Helmet, { children: _jsx("script", { src: embedScript }) }), _jsxs(Card, { className: "w-full p-8 mb-8 text-wfGray-800 border-salmon-600 border-2 text-left rounded-none", children: [_jsx("h2", { className: "text-xl", children: "Preview and enable member experience features here." }), (business === null || business === void 0 ? void 0 : business.status) == "Approved" && (_jsx("p", { className: "mt-4 mb-8", children: "Your customers won't see your enabled features until you launch." })), primaryActionLoading && (_jsx("p", { className: "prose prose-lg mb-4 w-full", children: _jsx("span", { className: "font-heavy", children: "Saving..." }) })), preferences.map((preference) => (_jsxs("div", { className: "mt-4 mb-8 text-md flex", children: [_jsx(ExperienceScreenUpdatePreference, { option: preference.option, required: preference.required }), _jsxs("div", { className: "inline font-sans normal-case", children: [_jsxs("label", { htmlFor: preference.option, className: "font-heavy cursor-pointer", children: [preference.label, (preference.required ? " (Required)" : (preference.recommended ? " (Recommended)" : ""))] }), " ", _jsx("div", { className: "font-sans normal-case my-2", children: preference.description }), preference.previewKind && (preference.option === 'enableEmailUpsell' ? (_jsxs("div", { className: "w-full flex flex-col gap-2", children: [_jsxs("div", { className: "flex gap-2 items-center", children: [_jsx("span", { className: "min-w-[120px]", children: "Online email:" }), _jsx("a", { href: "javascript:void(0)", className: "px-3 py-1 min-w-max block active:relative active:-left-2 active:-bottom-2 no-underline static bg-white rounded-none border focus:outline-none border-wfGray-600 hover:border-wfGray-800 text-wfGray-800", onClick: () => showPreview(preference.option, 'online'), children: "View" }), _jsx("a", { href: "javascript:void(0)", className: "px-3 py-1 min-w-max block active:relative active:-left-2 active:-bottom-2 no-underline static bg-white rounded-none border focus:outline-none border-wfGray-600 hover:border-wfGray-800 text-wfGray-800", onClick: () => openLetterEditor('ONLINE_UPSELL'), children: "Edit" })] }), _jsxs("div", { className: "flex gap-2 items-center", children: [_jsx("span", { className: "min-w-[120px]", children: "In-store email:" }), _jsx("a", { href: "javascript:void(0)", className: "px-3 py-1 min-w-max block active:relative active:-left-2 active:-bottom-2 no-underline static bg-white rounded-none border focus:outline-none border-wfGray-600 hover:border-wfGray-800 text-wfGray-800", onClick: () => showPreview(preference.option, 'instore'), children: "View" }), _jsx("a", { href: "javascript:void(0)", className: "px-3 py-1 min-w-max block active:relative active:-left-2 active:-bottom-2 no-underline static bg-white rounded-none border focus:outline-none border-wfGray-600 hover:border-wfGray-800 text-wfGray-800", onClick: () => openLetterEditor('IN_STORE_UPSELL'), children: "Edit" })] })] })) : (_jsxs("div", { className: "w-full flex relative gap-2", children: [_jsx("a", { href: "javascript:void(0)", className: "experience-screen-preview px-4 py-2 min-w-max block active:relative active:-left-2 active:-bottom-2 no-underline static bg-white rounded-none border focus:outline-none border-wfGray-600 hover:border-wfGray-800 text-wfGray-800", "data-preference": preference.label, onClick: () => showPreview(preference.option), children: primaryActionLoading ? "Saving..." : (preference.previewKind === "video" ? "Watch demo" :
                                                    preference.previewKind === "message" ? "View message" :
                                                        preference.previewKind === "popup" ? "See upsell" :
                                                            preference.previewKind === "live" ? "Preview in theme" : "") }), preference.option === "enablePastCustomerUpsell" && (_jsx("a", { href: "javascript:void(0)", className: "px-4 py-2 min-w-max block active:relative active:-left-2 active:-bottom-2 no-underline static bg-white rounded-none border focus:outline-none border-wfGray-600 hover:border-wfGray-800 text-wfGray-800", onClick: () => openLetterEditor('ANNOUNCEMENT'), children: "Edit message" }))] }))), preference.channel === "navigation" || preference.channel === "faq" ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "mt-4", children: "If this doesn't look great, uncheck it and add this link to your navigation directly:" }), _jsx("code", { className: "block", children: preference.channel === "navigation" ? joinLinkFor(business.alias, undefined, { utm_campaign: "wf-website", }) : "/community/withfriends" }), _jsxs("p", { className: "mt-4", children: ["Instructions: ", _jsx("a", { href: "https://help.withfriends.co/9110286-adding-membership-links-to-your-shopify-navigation", className: "ml-2 underline", onClick: (e) => { e.preventDefault(); visitLink("https://help.withfriends.co/9110286-adding-membership-links-to-your-shopify-navigation"); }, children: "Adding links to my nav bar" })] })] })) : preference.channel === "badge" ? (_jsxs("div", { className: "mt-2 flex flex-col p-4", children: [_jsx("h3", { className: "text-lg font-bold mb-4", children: "Settings" }), _jsxs("div", { className: "flex items-center mb-4", children: [_jsx("label", { htmlFor: "text-left", className: "text-sm w-40", children: "Text (left)" }), _jsx(TextInput, { onChange: updateBadgeText1, value: business.theme.badgeText1, className: "ml-4 text-sm p-2" })] }), _jsxs("div", { className: "flex items-center mb-4", children: [_jsx("label", { htmlFor: "text-right", className: "text-sm w-40", children: "Text (right)" }), _jsx(TextInput, { onChange: updateBadgeText2, value: business.theme.badgeText2, className: "ml-4 text-sm p-2" })] }), _jsxs("div", { className: "flex items-center mb-4", children: [_jsx("label", { htmlFor: "invert-colors", className: "text-sm w-40", children: "Invert colors" }), _jsx("input", { type: "checkbox", id: "invert-colors", name: "invert-colors", className: "ml-4", onChange: updateBadgeInverted, checked: business.theme.badgeInverted })] }), _jsxs("div", { className: "flex items-center mb-4", children: [_jsx("label", { htmlFor: "border-radius", className: "text-sm w-40", children: "Corner roundness" }), _jsx(TextInput, { onChange: updateCornerRoundness, value: business.theme.cornerRoundness, className: "ml-4 text-sm p-2", isNumber: true })] }), _jsxs("div", { className: "flex items-center mb-4", children: [_jsx("label", { htmlFor: "side", className: "text-sm w-40", children: "Side" }), _jsxs("select", { id: "side", name: "side", className: "ml-4 text-sm p-2 border border-gray-300", onChange: updateBadgeAlignment, value: business.theme.badgeAlignment, children: [_jsx("option", { value: "LEFT", children: "Left" }), _jsx("option", { value: "RIGHT", children: "Right" })] })] }), _jsxs("div", { className: "flex items-center", children: [_jsx("label", { htmlFor: "font-size", className: "text-sm w-40", children: "Size" }), _jsx(TextInput, { id: "font-size", name: "font-size", className: "ml-4 text-sm p-2", onChange: updateBadgeFontSize, value: business.theme.badgeFontSize, isNumber: true })] })] })) : preference.option == "enablePastCustomerUpsell" ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "mt-4", children: "Limited to 100 customers a day" }), _jsx("p", { className: "mt-4", children: "We automatically get customers from your store data. If you have additional contacts that you want to send the announcement to, you can import them here. It's ok to import the same contacts multiple times, they'll only get the announcement once." }), _jsx(Button, { variant: ButtonVariant.Salmon, className: "my-2", onClick: () => {
                                                    setShowCSV(!showCSV);
                                                }, children: "Import Contacts" }), showCSV && _jsx(CSVImport, { alias: business.alias })] })) : (preference.option == "enableAddToCartUpsell" || preference.option == "enableSidebarUpsell") ? (_jsx("p", { className: "mt-4", children: _jsx("strong", { children: "Press add to cart in the preview to see this in action" }) })) : null] })] }))), (_jsx("div", { className: "my-4 text-md grid grid-cols-12 gap-2", children: _jsx("div", { className: "block font-sans normal-case col-span-11", children: popupBlockerWarning }) }))] }), showLetterEditor && (_jsx(ModalConfirmation, { message: editingLetterKind === 'ANNOUNCEMENT' ? "Edit announcement message" : (editingLetterKind === 'ONLINE_UPSELL' ? "Edit email upsell (Online)" : "Edit email upsell (In-store)"), buttonConfirm: saveLetter, buttonConfirmContents: primaryActionLoading ? "Saving..." : "Save", buttonReject: closeLetterEditor, buttonRejectContents: "Cancel", wide: true, children: _jsx("div", { className: "mt-4", children: _jsx(DropboxPaperEditor, { emailSafe: true, value: letterDraft, onChange: onLetterChange, className: "max-w-4xl" }) }) }))] }));
};
export default ExperienceScreenPreferences;
